/**
 * Style/category SEO pages use `{style}/{category}` URL slugs.
 * Same prefixes as ProductAndCategory `specialCategories`.
 */
export const STYLE_CATEGORY_PREFIXES = [
  'mid-century-furniture',
  'small-space-furniture',
  'modern',
  'new',
];

/**
 * Maps a category/collection page slug to the value stored in Algolia's
 * `categories` facet. Composite slugs use `{prefix}/{name}` in URLs but
 * Algolia stores them without the slash (e.g. `collections/carin` →
 * `collectionscarin`, `modern/sofas-and-sectionals` → `modernsofas-and-sectionals`).
 */
export const getAlgoliaCategoryFilterSlug = slug => {
  if (!slug) return slug;

  if (slug.includes('/')) {
    return slug.replace('/', '');
  }

  return slug;
};

/** Maps Alexandria filter ids (camelCase) to Algolia facet attribute names. */
export const getAlgoliaFacetAttribute = filterId =>
  filterId ? filterId.toLowerCase() : filterId;

export const isCollectionPageSlug = slug =>
  typeof slug === 'string' && slug.startsWith('collections/');

export const isStyleCategoryPrefix = prefix =>
  STYLE_CATEGORY_PREFIXES.includes(prefix);

/**
 * Returns `{ stylePrefix, categorySlug }` for pages like
 * `modern/sofas-and-sectionals`, or null for regular category slugs.
 */
export const parseStyleCategoryPageSlug = slug => {
  if (!slug || !slug.includes('/')) return null;

  const stylePrefix = STYLE_CATEGORY_PREFIXES.find(prefix =>
    slug.startsWith(`${prefix}/`)
  );
  if (!stylePrefix) return null;

  return {
    stylePrefix,
    categorySlug: slug.slice(stylePrefix.length + 1),
  };
};

// Hardcoded sidebar widgets — not dynamic Alexandria facet filters.
// Shared by CategoryAlgoliaSidebar / MobileFilters / hasSidebarFacetFilters.
export const SKIP_FILTER_IDS = new Set([
  'quickship',
  'color',
  'widthRange',
  'search',
]);

/** True when Alexandria returned at least one sidebar facet filter with options. */
export const hasSidebarFacetFilters = filters =>
  (filters || []).some(
    filter => !SKIP_FILTER_IDS.has(filter?.id) && filter?.options?.length > 0
  );

/**
 * When a style/category slug has metadata but empty facet filters (often on
 * non-prod Alexandria caches), reuse the parent category's filters.
 */
export const mergeParentFiltersIntoCategoryPage = (
  categoryPage,
  parentFilters
) => {
  if (!categoryPage || hasSidebarFacetFilters(categoryPage.filters)) {
    return categoryPage;
  }
  if (!hasSidebarFacetFilters(parentFilters)) {
    return categoryPage;
  }

  return {
    ...categoryPage,
    filters: parentFilters,
  };
};
